import React, { useState, useEffect } from 'react';
import { Search, User, Award, Download } from 'lucide-react';
import api from '@/api/api';

const BonafideCertificate = () => {
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [includeFeeStructure, setIncludeFeeStructure] = useState(false);
  const [includeBankDetails, setIncludeBankDetails] = useState(false);

  useEffect(() => {
    fetchEligibleStudents();
  }, []);

  const fetchEligibleStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/fees/bonafide-eligible-students');
      setStudents(response.data.eligible_students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to fetch student data');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleGenerateCertificate = async (student) => {
    setGeneratingFor(student.id);
    try {
      const response = await api.get(
        `/fees/bonafide-certificate/${student.id}`,
        {
          params: {
            include_fee_structure: includeFeeStructure,
            include_bank_details: includeBankDetails,
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bonafide_certificate_${student.admission_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert(error.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setGeneratingFor(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bonafide Certificate</h1>
        <p className="text-sm text-gray-500 mt-1">Generate bonafide certificates for admitted students</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Award className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-500">Total Eligible Students</p>
              <p className="text-2xl font-bold text-gray-900">{students.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Generation options */}
      <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Certificate Options</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeFeeStructure}
              onChange={(e) => setIncludeFeeStructure(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
              data-testid="bonafide-include-fee" />
            <span className="text-sm text-gray-700">Include Fee Structure</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeBankDetails}
              onChange={(e) => setIncludeBankDetails(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
              data-testid="bonafide-include-bank" />
            <span className="text-sm text-gray-700">Include Bank Details</span>
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Configure these in <a href="/certificates/settings" className="text-blue-600 hover:underline">Certificate Settings</a> first.
        </p>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start">
          <Award className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Eligibility:</strong> All admitted students with installments set up are eligible for bonafide certificate.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search students by name or admission number..."
            className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="bonafide-search"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Details</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">Loading students...</td></tr>
            ) : filteredStudents.length === 0 ? (
              <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500">No students found</td></tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{student.full_name}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.admission_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{student.course_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{student.session}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleGenerateCertificate(student)}
                      disabled={generatingFor === student.id}
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-900 font-medium text-sm"
                      data-testid={`bonafide-download-${student.id}`}
                    >
                      <Download className="h-4 w-4" />
                      {generatingFor === student.id ? 'Generating...' : 'Download Certificate'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BonafideCertificate;
